import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import pinPrompt from "../utils/pinPrompt";
import { API_BASE, apiJson, loadFormCatalog } from "../utils/taxCertificates";
import { emailSenderHeaders } from "../utils/emailSender";

/**
 * "Request a sales-tax exemption certificate by email."
 *
 * Emails the customer the Kansas form(s) the office ticks (ST-28D / ST-28A /
 * ST-28M, links to the KDOR PDFs) plus a single-customer upload link
 * (/tax-certificate/<token>) so the signed copy lands straight on the
 * customer record. Defaults follow Annie's rule: a Kansas address gets
 * ST-28D, anything else gets ST-28D + ST-28M ("not sure? send both").
 *
 * Props:
 *   customer   {customer_id, name, contact_name, email, state}
 *   contacts   optional [{contact_id, name, email, role}] to pick a recipient
 *   suggestedFormTypes  optional string[] from the API
 *   onClose()  onSent(result)
 */
export default function TaxCertRequestModal({ customer, contacts = [], suggestedFormTypes, onClose, onSent }) {
  const [catalog, setCatalog] = useState(null);
  const [toEmail, setToEmail] = useState(customer?.email || "");
  const [ccEmails, setCcEmails] = useState("");
  const [contactName, setContactName] = useState(customer?.contact_name || "");
  const [formTypes, setFormTypes] = useState(() => new Set(suggestedFormTypes || ["ST-28D", "ST-28M"]));
  const [note, setNote] = useState("");
  const [expiresDays, setExpiresDays] = useState(60);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    loadFormCatalog().then(setCatalog).catch(() => setCatalog({ forms: [], requestable: ["ST-28D", "ST-28A", "ST-28M"] }));
  }, []);

  const recipientOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    const push = (email, label) => {
      const e = (email || "").trim();
      if (!e || seen.has(e.toLowerCase())) return;
      seen.add(e.toLowerCase());
      out.push({ email: e, label });
    };
    push(customer?.email, `${customer?.contact_name || "Main contact"} (main)`);
    (contacts || []).forEach((c) => push(c.email, `${c.name}${c.role ? ` — ${c.role}` : ""}`));
    return out;
  }, [customer, contacts]);

  const requestable = catalog?.requestable || ["ST-28D", "ST-28A", "ST-28M"];
  const formsByCode = useMemo(() => {
    const m = {};
    (catalog?.forms || []).forEach((f) => { m[f.code] = f; });
    return m;
  }, [catalog]);

  function toggleForm(code) {
    setFormTypes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  }

  async function send() {
    setErr(null);
    if (!toEmail.trim()) { setErr("A recipient email is required."); return; }
    if (formTypes.size === 0) { setErr("Tick at least one form to send."); return; }
    const pin = await pinPrompt("Enter your PIN to send the certificate request:");
    if (!pin) return;
    setBusy(true);
    try {
      const body = {
        pin,
        to_email: toEmail.trim(),
        cc_emails: ccEmails.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean),
        contact_name: contactName.trim() || null,
        form_types: requestable.filter((c) => formTypes.has(c)),
        note: note.trim() || null,
        expires_days: Number(expiresDays) || 60,
      };
      const r = await apiJson(`${API_BASE}/customers/${customer.customer_id}/tax_certificates/request`, {
        method: "POST", body: JSON.stringify(body),
        headers: { "Content-Type": "application/json", ...emailSenderHeaders() },
      });
      onSent && onSent(r);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal show onHide={busy ? undefined : onClose} size="lg" backdrop="static" centered>
      <Modal.Header closeButton={!busy}>
        <Modal.Title>Request exemption certificate — {customer?.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row g-3">
          <div className="col-md-7">
            <label className="form-label">Send to</label>
            {recipientOptions.length > 0 && (
              <select className="form-select mb-1" value={recipientOptions.some((o) => o.email === toEmail) ? toEmail : ""}
                      onChange={(e) => { if (e.target.value) setToEmail(e.target.value); }}>
                <option value="">— pick a contact or type below —</option>
                {recipientOptions.map((o) => <option key={o.email} value={o.email}>{o.label} &lt;{o.email}&gt;</option>)}
              </select>
            )}
            <input type="email" className="form-control" value={toEmail} onChange={(e) => setToEmail(e.target.value)} placeholder="email@customer.com" />
          </div>
          <div className="col-md-5">
            <label className="form-label">Greeting name</label>
            <input type="text" className="form-control" value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Hi …," />
          </div>
          <div className="col-md-7">
            <label className="form-label">CC (optional, comma-separated)</label>
            <input type="text" className="form-control" value={ccEmails} onChange={(e) => setCcEmails(e.target.value)} />
          </div>
          <div className="col-md-5">
            <label className="form-label">Upload link valid for</label>
            <div className="input-group">
              <input type="number" min={1} max={365} className="form-control" value={expiresDays} onChange={(e) => setExpiresDays(e.target.value)} />
              <span className="input-group-text">days</span>
            </div>
          </div>
          <div className="col-12">
            <label className="form-label mb-1">Forms to include in the email</label>
            <div className="form-text mb-2">
              Kansas-registered customers: ST-28D. Out-of-state customers with no Kansas tax number: ST-28M. Not sure? Send both — the customer returns only the one that fits.
              {customer?.state ? <> Address state on file: <strong>{customer.state}</strong>.</> : <> No state on file for this customer.</>}
            </div>
            {requestable.map((code) => {
              const f = formsByCode[code] || { code, title: "", who: "" };
              return (
                <div className="form-check mb-2" key={code}>
                  <input className="form-check-input" type="checkbox" id={`req-form-${code}`} checked={formTypes.has(code)} onChange={() => toggleForm(code)} />
                  <label className="form-check-label" htmlFor={`req-form-${code}`}>
                    <strong>{code}</strong>{f.title ? ` — ${f.title}` : ""}
                    {f.who && <div className="text-muted small">{f.who}</div>}
                  </label>
                </div>
              );
            })}
          </div>
          <div className="col-12">
            <label className="form-label">Note to add to the email (optional)</label>
            <textarea className="form-control" rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                      placeholder="e.g. We need this on file before we can invoice your next order without tax." />
          </div>
          <div className="col-12">
            <div className="alert alert-light border small mb-0">
              The email goes out from <strong>info@kancraftmfg.com</strong> with links to the Kansas Department of Revenue PDFs, KanCraft's seller details for the form, and a private upload link for the signed copy. The customer can also reply with the signed copy attached.
            </div>
          </div>
          {err && <div className="col-12"><div className="alert alert-danger mb-0">{err}</div></div>}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn btn-primary" onClick={send} disabled={busy}>{busy ? "Sending…" : "Send request email"}</button>
      </Modal.Footer>
    </Modal>
  );
}
